import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { getTeacherClasses } from '../services/teacherClassService'
import { getStudentsByTeacherClass } from '../services/userService'
import { formatChordDetailed, userAnswerToLabel } from '../utils/chordFormatter'
import {
  clearLiveSessionCurrentRanking,
  clearLiveSessionFinalRanking,
  rewindLiveSessionExercise,
  advanceLiveSessionExercise,
  createLiveSession,
  getLiveSessionCombinedRanking,
  getLiveSessionCurrentRanking,
  getLiveSessionFinalRanking,
  getTeacherLiveSessions,
  saveLiveSessionCombinedRanking,
  saveLiveSessionCurrentRanking,
  saveLiveSessionFinalRanking,
  setLiveSessionStatus,
  subscribeLiveSessionById
} from '../services/liveSessionService'
import { getAttemptsByLiveSession } from '../services/attemptService'
import {
  loadLiveSessionDraft,
  replaceLiveSessionQueueByClass,
  setSelectedLiveSessionClass
} from '../utils/liveSessionDraft'
import { LIVE_REMOTE_ENABLED } from '../config/liveFeatures'
import './TeacherLiveSessions.css'

const SCORE_VISIBLE_THRESHOLD = 40
const RANKING_THROTTLE_MS = 45000
const RANKING_CACHE_MAX_AGE_MS = 120000
const HISTORY_PAGE_SIZE = 6
const MOCK_HISTORY_COUNT = 5
const RANKING_SCOPE_CURRENT = 'current'
const RANKING_SCOPE_COMBINED = 'combined'
const LIVE_INPUT_MODES = [
  { value: 'functions', label: 'Fonctions tonales' },
  { value: 'qcm', label: 'QCM' },
  { value: 'full', label: 'Entrée complète' }
]

function normalizeLiveInputMode(mode) {
  const value = String(mode || '').trim()
  return LIVE_INPUT_MODES.some((item) => item.value === value) ? value : 'full'
}

function getLiveInputModeLabel(mode) {
  const normalized = normalizeLiveInputMode(mode)
  return LIVE_INPUT_MODES.find((item) => item.value === normalized)?.label || 'Entrée complète'
}

function getCurrentExercise(session) {
  const queue = Array.isArray(session?.exerciseQueue) ? session.exerciseQueue : []
  const index = Number(session?.currentExerciseIndex || 0)
  return queue[index] || null
}

function answerLabel(answer) {
  return userAnswerToLabel(answer)
}

function buildLabelDebugSignature(value) {
  if (typeof value !== 'string') return null
  const chars = Array.from(value)
  return {
    raw: value,
    trimmed: value.trim(),
    lower: value.trim().toLowerCase(),
    length: value.length,
    codes: chars.map((char) => char.charCodeAt(0))
  }
}

function buildStrongPointFromAttempts(attemptsByExercise = []) {
  const chordStats = new Map()
  ;(attemptsByExercise || []).forEach((attempt) => {
    const correctAnswers = Array.isArray(attempt?.correctAnswers) ? attempt.correctAnswers : []
    const userAnswers = Array.isArray(attempt?.userAnswers) ? attempt.userAnswers : []
    const length = Math.max(correctAnswers.length, userAnswers.length)
    for (let i = 0; i < length; i += 1) {
      const correct = answerLabel(correctAnswers[i])
      if (!correct || correct === 'Non répondu') continue
      const user = answerLabel(userAnswers[i])
      const key = String(correct).trim()
      if (!key) continue
      const prev = chordStats.get(key) || { label: key, total: 0, correct: 0 }
      prev.total += 1
      if (user === correct) prev.correct += 1
      chordStats.set(key, prev)
    }
  })
  const ranked = Array.from(chordStats.values())
    .filter((row) => Number(row.total || 0) > 0)
    .map((row) => ({
      ...row,
      percent: Math.round((Number(row.correct || 0) / Math.max(1, Number(row.total || 0))) * 100)
    }))
    .sort((a, b) => {
      const percentDiff = Number(b.percent || 0) - Number(a.percent || 0)
      if (percentDiff !== 0) return percentDiff
      const totalDiff = Number(b.total || 0) - Number(a.total || 0)
      if (totalDiff !== 0) return totalDiff
      return String(a.label || '').localeCompare(String(b.label || ''), 'fr', { sensitivity: 'base' })
    })
  if (ranked.length === 0) return null
  const best = ranked[0]
  return {
    label: best.label,
    percent: best.percent,
    samples: best.total
  }
}

function computeSessionRanking(attempts = [], classStudents = [], exerciseIds = [], options = {}) {
  const mergeStrategy = String(options?.mergeStrategy || 'latest')
  const useBestScore = mergeStrategy === 'best_score'
  const studentMap = new Map((classStudents || []).map((student) => [student.id, student]))
  const validExerciseIds = (exerciseIds || []).map((id) => String(id || '')).filter(Boolean)
  const validSet = new Set(validExerciseIds)
  const latestByStudentExercise = new Map()

  ;(attempts || []).forEach((attempt) => {
    const studentId = String(attempt?.userId || '')
    const exerciseId = String(attempt?.exerciseId || '')
    if (!studentMap.has(studentId) || !validSet.has(exerciseId)) return
    if (!latestByStudentExercise.has(studentId)) latestByStudentExercise.set(studentId, new Map())
    const byExercise = latestByStudentExercise.get(studentId)
    const prev = byExercise.get(exerciseId)
    const prevTs = prev?.completedAt?.toDate?.()?.getTime?.() ?? 0
    const currTs = attempt?.completedAt?.toDate?.()?.getTime?.() ?? 0
    if (!prev) {
      byExercise.set(exerciseId, attempt)
      return
    }
    if (useBestScore) {
      const prevScore = Number(prev?.score || 0)
      const currScore = Number(attempt?.score || 0)
      if (currScore > prevScore || (currScore === prevScore && currTs >= prevTs)) {
        byExercise.set(exerciseId, attempt)
      }
      return
    }
    if (currTs >= prevTs) byExercise.set(exerciseId, attempt)
  })

  const ranking = []
  const totalExercises = validExerciseIds.length
  latestByStudentExercise.forEach((exerciseMap, studentId) => {
    const attemptsByExercise = validExerciseIds
      .map((exerciseId) => exerciseMap.get(exerciseId))
      .filter(Boolean)
    // Score sur tous les exercices : non rendu = 0 % (compté comme faux)
    const scores = validExerciseIds.map((exerciseId) => {
      const attempt = exerciseMap.get(exerciseId)
      return attempt ? Number(attempt?.score || 0) : 0
    })
    if (attemptsByExercise.length === 0) return
    const avgScore = totalExercises === 0 ? 0 : Math.round(scores.reduce((sum, value) => sum + value, 0) / totalExercises)
    const lastSubmissionAtMs = attemptsByExercise.reduce((acc, attempt) => {
      const ts = attempt?.completedAt?.toDate?.()?.getTime?.() ?? 0
      return Math.max(acc, ts)
    }, 0)
    const student = studentMap.get(studentId)
    ranking.push({
      userId: studentId,
      studentName: student?.displayName || student?.email || 'Élève',
      photoURL: student?.photoURL || null,
      score: avgScore,
      answeredExercises: attemptsByExercise.length,
      lastSubmissionAtMs,
      strongPoint: buildStrongPointFromAttempts(attemptsByExercise)
    })
  })

  ranking.sort((a, b) => {
    const scoreDiff = Number(b.score || 0) - Number(a.score || 0)
    if (scoreDiff !== 0) return scoreDiff
    const answeredDiff = Number(b.answeredExercises || 0) - Number(a.answeredExercises || 0)
    if (answeredDiff !== 0) return answeredDiff
    const dateDiff = Number(a.lastSubmissionAtMs || 0) - Number(b.lastSubmissionAtMs || 0)
    if (dateDiff !== 0) return dateDiff
    return String(a.studentName || '').localeCompare(String(b.studentName || ''), 'fr', { sensitivity: 'base' })
  })
  const ranked = ranking.map((row, index) => ({ ...row, rank: index + 1 }))
  return {
    ranking: ranked,
    submissions: ranked.length,
    classSize: classStudents.length,
    exerciseCount: validExerciseIds.length
  }
}

function createMockSessionRanking(classStudents = [], exerciseCount = 1, rankingType = 'provisional') {
  const mockStrongPoints = [
    { label: 'V7', percent: 100, samples: 4 },
    { label: 'ii7', percent: 93, samples: 5 },
    { label: 'I64', percent: 91, samples: 3 },
    { label: 'cad64', percent: 88, samples: 4 },
    { label: 'N6', percent: 86, samples: 3 },
    { label: 'Ger6', percent: 84, samples: 4 },
    { label: 'IV', percent: 82, samples: 5 },
    { label: 'vi', percent: 80, samples: 3 }
  ]
  const students = Array.isArray(classStudents) ? classStudents : []
  const fallbackStudents = Array.from({ length: Math.max(6, students.length || 0) }).map((_, idx) => ({
    id: `mock-ranking-${idx + 1}`,
    displayName: `Eleve ${idx + 1}`
  }))
  const participants = (students.length > 0 ? students : fallbackStudents).slice(0, 30)
  const ranking = participants.map((student, index) => ({
    rank: index + 1,
    userId: student.id,
    studentName: student?.displayName || student?.email || `Eleve ${index + 1}`,
    photoURL: student?.photoURL || null,
    score: Math.max(34, 96 - (index * 5)),
    answeredExercises: Math.max(1, exerciseCount),
    strongPoint: mockStrongPoints[index % mockStrongPoints.length]
  }))
  return {
    ranking,
    submissions: ranking.length,
    classSize: students.length || ranking.length,
    exerciseCount: Math.max(1, exerciseCount),
    rankingType,
    mock: true,
    computedAtMs: Date.now()
  }
}

function applyCompetitionRanking(rows = []) {
  let previousScore = null
  let previousRank = 0
  return (rows || []).map((row, index) => {
    const score = Number(row?.score || 0)
    const displayRank = (index > 0 && score === previousScore) ? previousRank : (index + 1)
    previousScore = score
    previousRank = displayRank
    return { ...row, displayRank }
  })
}

function formatRankingUpdateTime(tsMs) {
  if (!Number.isFinite(Number(tsMs)) || Number(tsMs) <= 0) return '—'
  const date = new Date(Number(tsMs))
  return new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)
}

function getTimestampMs(value) {
  return value?.toDate?.()?.getTime?.() ?? 0
}

function formatSessionDateTime(tsMs) {
  if (!Number.isFinite(Number(tsMs)) || Number(tsMs) <= 0) return '—'
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(Number(tsMs)))
}

function formatSessionDuration(minutes) {
  const safeMinutes = Math.max(0, Number(minutes || 0))
  if (!Number.isFinite(safeMinutes) || safeMinutes <= 0) return '—'
  if (safeMinutes < 60) return `${safeMinutes} min`
  const h = Math.floor(safeMinutes / 60)
  const m = safeMinutes % 60
  return m > 0 ? `${h} h ${m} min` : `${h} h`
}

function statusMeta(status) {
  if (status === 'collecting') return { label: 'Session en cours', tone: 'collecting' }
  if (status === 'correction_released') return { label: 'Correction', tone: 'correction' }
  if (status === 'closed') return { label: 'Clôturée', tone: 'closed' }
  return { label: String(status || 'Inconnu'), tone: 'unknown' }
}

function getStudentInitials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (parts.length === 0) return 'EL'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase()
}

function enrichRankingRowsWithPhotos(rows, students) {
  const map = new Map((students || []).map((s) => [String(s.id), s.photoURL || null]))
  return (rows || []).map((row) => {
    const uid = String(row?.userId || '')
    const fromStudent = uid ? map.get(uid) : null
    const url = (fromStudent && String(fromStudent).trim()) || row.photoURL || null
    return { ...row, photoURL: url || null }
  })
}

async function enrichRankingWithClassPhotos(ranking, teacherClassId) {
  if (!teacherClassId || !Array.isArray(ranking) || ranking.length === 0) return ranking
  try {
    const students = await getStudentsByTeacherClass(teacherClassId)
    return enrichRankingRowsWithPhotos(ranking, students)
  } catch {
    return ranking
  }
}

function RankingAvatarCell({ photoURL, name, variant = 'podium' }) {
  const initials = getStudentInitials(name)
  if (variant === 'podium') {
    if (photoURL) {
      return (
        <span className="teacher-live-podium-avatar teacher-live-podium-avatar--photo">
          <img src={photoURL} alt="" referrerPolicy="no-referrer" />
        </span>
      )
    }
    return <span className="teacher-live-podium-avatar">{initials}</span>
  }
  if (photoURL) {
    return (
      <span className="teacher-live-ranking-avatar-cell teacher-live-ranking-avatar-cell--photo">
        <img src={photoURL} alt="" referrerPolicy="no-referrer" />
      </span>
    )
  }
  return <span className="teacher-live-ranking-avatar-cell">{initials}</span>
}

function SessionRankingLeaderboardBlock({ rows, exerciseCount, loading }) {
  const [showBelowThreshold, setShowBelowThreshold] = useState(false)
  const rankingVisibleRows = useMemo(
    () => rows.filter((row) => Number(row?.score || 0) >= SCORE_VISIBLE_THRESHOLD),
    [rows]
  )
  const rankingRowsToDisplay = useMemo(
    () => (showBelowThreshold ? rows : rankingVisibleRows),
    [showBelowThreshold, rows, rankingVisibleRows]
  )
  const rankingTop3 = useMemo(() => rows.slice(0, 3), [rows])
  const podiumRows = useMemo(() => ([
    { slot: 'second', icon: '🥈', row: rankingTop3[1] || null },
    { slot: 'first', icon: '👑', row: rankingTop3[0] || null },
    { slot: 'third', icon: '🥉', row: rankingTop3[2] || null }
  ]), [rankingTop3])

  if (loading && rows.length === 0) {
    return (
      <div className="teacher-live-ranking-skeleton">
        <div />
        <div />
        <div />
        <div />
      </div>
    )
  }
  if (rows.length === 0) return null

  return (
    <>
      <div className="teacher-live-podium">
        {podiumRows.filter((entry) => entry.row && (showBelowThreshold || Number(entry.row?.score ?? 0) >= SCORE_VISIBLE_THRESHOLD)).map((entry) => (
          <article
            key={`podium-${entry.row.userId || entry.row.studentName}-${entry.slot}`}
            className={`teacher-live-podium-card slot-${entry.slot}`}
          >
            <div className="teacher-live-podium-head">
              <span className="teacher-live-podium-icon" aria-hidden>{entry.icon}</span>
              <span className="teacher-live-podium-rank">#{entry.row.displayRank}</span>
            </div>
            <RankingAvatarCell variant="podium" photoURL={entry.row.photoURL} name={entry.row.studentName} />
            <strong>{entry.row.studentName}</strong>
            <span className="teacher-live-podium-score">{entry.row.score}%</span>
            {entry.row?.strongPoint?.label && (
              <span className="teacher-live-podium-point">
                🎯{' '}
                {/^[IVXLCDM]+°?$/i.test(String(entry.row.strongPoint.label)) ? (
                  <span className="degree-roman">{entry.row.strongPoint.label}</span>
                ) : (
                  entry.row.strongPoint.label
                )}{' '}
                {entry.row.strongPoint.percent}%
              </span>
            )}
          </article>
        ))}
      </div>

      <div className="teacher-live-ranking-table-wrap">
        <table className="teacher-live-ranking-table">
          <thead>
            <tr>
              <th>Rang</th>
              <th>Élève</th>
              <th>Score global</th>
              <th>Exercices</th>
              <th>Point fort</th>
            </tr>
          </thead>
          <tbody>
            {rankingRowsToDisplay.map((row) => (
              <tr key={`${row.userId || row.studentName}-${row.displayRank}`} className={row.score < SCORE_VISIBLE_THRESHOLD ? 'is-under-threshold' : ''}>
                <td>#{row.displayRank}</td>
                <td>
                  <div className="teacher-live-ranking-student-cell">
                    <RankingAvatarCell variant="table" photoURL={row.photoURL} name={row.studentName} />
                    <span className="teacher-live-ranking-student-name">{row.studentName}</span>
                  </div>
                </td>
                <td>
                  <div className="teacher-live-score-cell">
                    <span>{row.score}%</span>
                    <div className="teacher-live-score-bar">
                      <div style={{ width: `${Math.max(0, Math.min(100, Number(row.score || 0)))}%` }} />
                    </div>
                  </div>
                </td>
                <td>{row.answeredExercises || 0}/{exerciseCount || 0}</td>
                <td>
                  {row?.strongPoint?.label ? (
                    <span className="teacher-live-strong-point">
                      <span aria-hidden>🎯</span>
                      {/^[IVXLCDM]+°?$/i.test(String(row.strongPoint.label)) ? (
                        <span className="degree-roman">{row.strongPoint.label}</span>
                      ) : (
                        row.strongPoint.label
                      )}{' '}
                      {row.strongPoint.percent}%
                    </span>
                  ) : (
                    <span className="teacher-live-muted">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="teacher-live-muted">
        {Math.max(0, rows.length - rankingVisibleRows.length)} élève(s) sous le seuil.
        <button
          type="button"
          className="teacher-live-under-toggle"
          onClick={() => setShowBelowThreshold((prev) => !prev)}
        >
          {showBelowThreshold ? 'Masquer les sous 40%' : 'Afficher tout'}
        </button>
      </p>
    </>
  )
}

function getSessionExerciseIds(session) {
  return (Array.isArray(session?.exerciseQueue) ? session.exerciseQueue : [])
    .map((item) => String(item?.exerciseId || '').trim())
    .filter(Boolean)
}

function haveStrictSamePlaylist(leftSession, rightSession) {
  const leftIds = getSessionExerciseIds(leftSession)
  const rightIds = getSessionExerciseIds(rightSession)
  if (leftIds.length === 0 || leftIds.length !== rightIds.length) return false
  return leftIds.every((id, index) => id === rightIds[index])
}

function TeacherLiveSessions() {
  const { user, userData } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [classes, setClasses] = useState([])
  const [sessions, setSessions] = useState([])
  const [selectedClassId, setSelectedClassId] = useState(() => loadLiveSessionDraft().selectedClassId || '')
  const [queueByClass, setQueueByClass] = useState(() => loadLiveSessionDraft().queueByClass || {})
  const [selectedInputMode, setSelectedInputMode] = useState('full')
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [busyAction, setBusyAction] = useState('')
  const [error, setError] = useState('')
  const [loadingMockData, setLoadingMockData] = useState(false)
  const [sessionRanking, setSessionRanking] = useState(null)
  const [loadingSessionRanking, setLoadingSessionRanking] = useState(false)
  const [lastRankingRefreshAtMs, setLastRankingRefreshAtMs] = useState(0)
  const [rankingScope, setRankingScope] = useState(RANKING_SCOPE_CURRENT)
  const [combinedSourceSessionId, setCombinedSourceSessionId] = useState('')
  const [historyVisibleCount, setHistoryVisibleCount] = useState(HISTORY_PAGE_SIZE)
  const [mockHistorySessions, setMockHistorySessions] = useState([])
  const [archivedRanking, setArchivedRanking] = useState(null)
  const [loadingArchivedRanking, setLoadingArchivedRanking] = useState(false)
  /** Carte historique sous laquelle afficher le classement (celle du dernier « Voir classement »). */
  const [expandedArchivedSessionId, setExpandedArchivedSessionId] = useState(null)
  const [archivedRankingInlineError, setArchivedRankingInlineError] = useState(null)
  const [activeSessionLive, setActiveSessionLive] = useState(null)
  const liveRankingSectionRef = useRef(null)
  const archivedRankingRequestRef = useRef(0)

  const activeSession = useMemo(
    () => (sessions || []).find((s) => s.status === 'collecting' || s.status === 'correction_released') || null,
    [sessions]
  )
  /** Session active à jour en temps réel (abonnement Firestore), pour un classement provisoire sur toutes les questions déjà ouvertes. */
  const effectiveActiveSession = activeSessionLive && activeSessionLive.id === activeSession?.id
    ? activeSessionLive
    : activeSession

  const queue = useMemo(
    () => (selectedClassId ? (queueByClass[selectedClassId] || []) : []),
    [queueByClass, selectedClassId]
  )
  const selectedClass = useMemo(
    () => classes.find((item) => item.id === selectedClassId) || null,
    [classes, selectedClassId]
  )
  const selectedClassLabel = selectedClass?.name || selectedClass?.code || selectedClass?.id || ''
  const currentExercise = getCurrentExercise(effectiveActiveSession)
  const activeSessionQueue = useMemo(
    () => (Array.isArray(effectiveActiveSession?.exerciseQueue) ? effectiveActiveSession.exerciseQueue : []),
    [effectiveActiveSession?.exerciseQueue]
  )
  const activeSessionExerciseIds = useMemo(
    () => activeSessionQueue.map((item) => String(item?.exerciseId || '')).filter(Boolean),
    [activeSessionQueue]
  )
  const activeSessionCurrentIndex = Number(effectiveActiveSession?.currentExerciseIndex || 0)
  const isLastExercise = activeSessionExerciseIds.length > 0 && activeSessionCurrentIndex >= (activeSessionExerciseIds.length - 1)
  const canPublishFinalRanking = effectiveActiveSession?.status === 'correction_released' && isLastExercise
  const activeSessionUpdatedAtMs = effectiveActiveSession?.updatedAt?.toDate?.()?.getTime?.()
    ?? effectiveActiveSession?.createdAt?.toDate?.()?.getTime?.()
    ?? 0
  const rankingViewRequested = String(searchParams.get('view') || '').trim() === 'ranking'
  const rankingSessionIdRequested = String(searchParams.get('session') || '').trim()
  const rankingRows = useMemo(
    () => applyCompetitionRanking(Array.isArray(sessionRanking?.ranking) ? sessionRanking.ranking : []),
    [sessionRanking?.ranking]
  )
  const rankingAverage = useMemo(() => {
    if (rankingRows.length === 0) return 0
    const total = rankingRows.reduce((sum, row) => sum + Number(row?.score || 0), 0)
    return Math.round(total / rankingRows.length)
  }, [rankingRows])
  const rankingAboveThresholdPct = useMemo(() => {
    if (rankingRows.length === 0) return 0
    const visible = rankingRows.filter((row) => Number(row?.score || 0) >= SCORE_VISIBLE_THRESHOLD)
    return Math.round((visible.length / rankingRows.length) * 100)
  }, [rankingRows])
  const classLabelById = useMemo(() => (
    (classes || []).reduce((acc, item) => {
      acc[item.id] = item?.name || item?.code || item?.id || item.id
      return acc
    }, {})
  ), [classes])
  const historySessions = useMemo(() => {
    const realClosed = (sessions || []).filter((s) => s.status === 'closed')
    const merged = [...mockHistorySessions, ...realClosed]
    return merged.sort((a, b) => {
      const aClosed = Number(a?.summary?.closedAtMs || 0) || Number(a?.closedAtMs || 0) || getTimestampMs(a?.closedAt) || getTimestampMs(a?.updatedAt)
      const bClosed = Number(b?.summary?.closedAtMs || 0) || Number(b?.closedAtMs || 0) || getTimestampMs(b?.closedAt) || getTimestampMs(b?.updatedAt)
      return bClosed - aClosed
    })
  }, [sessions, mockHistorySessions])
  const historyHasMore = historyVisibleCount < historySessions.length
  const visibleHistorySessions = useMemo(
    () => historySessions.slice(0, historyVisibleCount),
    [historySessions, historyVisibleCount]
  )
  const combinedRankingCandidates = useMemo(() => {
    if (!effectiveActiveSession?.id) return []
    return historySessions.filter((session) => {
      if (!session?.id || String(session.id).startsWith('mock-history-')) return false
      if (session.id === effectiveActiveSession.id) return false
      if (session.status !== 'closed') return false
      if (String(session.teacherClassId || '') !== String(effectiveActiveSession.teacherClassId || '')) return false
      return haveStrictSamePlaylist(effectiveActiveSession, session)
    })
  }, [historySessions, effectiveActiveSession])
  const selectedCombinedSourceSession = useMemo(
    () => combinedRankingCandidates.find((session) => session.id === combinedSourceSessionId) || null,
    [combinedRankingCandidates, combinedSourceSessionId]
  )
  const combinedSourceSessionUpdatedAtMs = Number(selectedCombinedSourceSession?.summary?.closedAtMs || 0)
    || Number(selectedCombinedSourceSession?.closedAtMs || 0)
    || getTimestampMs(selectedCombinedSourceSession?.closedAt)
    || getTimestampMs(selectedCombinedSourceSession?.updatedAt)
    || 0
  const archivedRankingRows = useMemo(
    () => applyCompetitionRanking(Array.isArray(archivedRanking?.rows) ? archivedRanking.rows : []),
    [archivedRanking?.rows]
  )
  const archivedRankingAverage = useMemo(() => {
    const r = archivedRankingRows
    if (r.length === 0) return Number(archivedRanking?.averageScore || 0)
    return Math.round(r.reduce((sum, row) => sum + Number(row?.score || 0), 0) / r.length)
  }, [archivedRankingRows, archivedRanking?.averageScore])
  const archivedRankingAboveThresholdPct = useMemo(() => {
    const r = archivedRankingRows
    if (r.length === 0) return Number(archivedRanking?.aboveThresholdPct || 0)
    return Math.round((r.filter((row) => Number(row?.score || 0) >= SCORE_VISIBLE_THRESHOLD).length / r.length) * 100)
  }, [archivedRankingRows, archivedRanking?.aboveThresholdPct])

  const loadAll = useCallback(async () => {
    if (!user?.uid) return
    setLoading(true)
    setError('')
    try {
      const [teacherClasses, teacherSessions] = await Promise.all([
        getTeacherClasses(user.uid),
        getTeacherLiveSessions(user.uid, 20)
      ])
      setClasses(teacherClasses || [])
      setSessions(teacherSessions || [])
      if ((teacherClasses || []).length > 0) {
        setSelectedClassId((prev) => {
          const hasPrev = prev && teacherClasses.some((item) => item.id === prev)
          if (hasPrev) return prev
          return teacherClasses[0].id
        })
      }
    } catch (err) {
      setError(err.message || 'Impossible de charger les sessions live.')
    } finally {
      setLoading(false)
    }
  }, [user?.uid])

  useEffect(() => {
    if (!user || userData?.role !== 'teacher') {
      navigate('/dashboard')
      return
    }
    loadAll()
  }, [user, userData?.role, navigate, loadAll])

  useEffect(() => {
    const sessionId = activeSession?.id
    if (!sessionId) {
      setActiveSessionLive(null)
      return () => {}
    }
    const unsubscribe = subscribeLiveSessionById(
      sessionId,
      (data) => setActiveSessionLive(data || null),
      (err) => {
        console.error('Erreur abonnement session live:', err)
        setActiveSessionLive(null)
      }
    )
    return () => {
      unsubscribe()
      setActiveSessionLive(null)
    }
  }, [activeSession?.id])

  useEffect(() => {
    setSelectedLiveSessionClass(selectedClassId, selectedClassLabel)
  }, [selectedClassId, selectedClassLabel])

  useEffect(() => {
    replaceLiveSessionQueueByClass(queueByClass)
  }, [queueByClass])

  useEffect(() => {
    const addExerciseId = String(searchParams.get('addExercise') || '').trim()
    const addExerciseTitle = String(searchParams.get('addTitle') || '').trim()
    const addClassId = String(searchParams.get('addClass') || '').trim()
    if (!addExerciseId) return
    const draft = loadLiveSessionDraft()
    const storedClassId = draft.selectedClassId
    const fallbackClassId = addClassId
      || selectedClassId
      || storedClassId
      || (Array.isArray(classes) && classes.length > 0 ? classes[0].id : '')
    if (!fallbackClassId) {
      setError('Sélectionnez d’abord une classe, puis ajoutez l’exercice à la session live.')
      return
    }
    setSelectedClassId((prev) => prev || fallbackClassId)
    setQueueByClass((prev) => {
      const existing = prev[fallbackClassId] || []
      if (existing.some((item) => item.exerciseId === addExerciseId)) return prev
      const nextForClass = [
        ...existing,
        { exerciseId: addExerciseId, exerciseTitle: addExerciseTitle || 'Exercice', order: existing.length }
      ]
      return { ...prev, [fallbackClassId]: nextForClass }
    })
    navigate('/dashboard/live-sessions', { replace: true })
  }, [searchParams, selectedClassId, classes, navigate])

  const createSessionNow = async () => {
    if (!selectedClassId || queue.length === 0 || !user?.uid) return
    setCreating(true)
    setError('')
    try {
      await createLiveSession({
        teacherId: user.uid,
        teacherClassId: selectedClassId,
        exerciseQueue: queue,
        inputMode: selectedInputMode
      })
      setQueueByClass((prev) => ({ ...prev, [selectedClassId]: [] }))
      await loadAll()
    } catch (err) {
      setError(err.message || 'Impossible de créer la session.')
    } finally {
      setCreating(false)
    }
  }

  const injectMockCurrentStats = async () => {
    if (!activeSession || !currentExercise?.exerciseId || !user?.uid) return
    setLoadingMockData(true)
    setError('')
    try {
      const classStudents = await getStudentsByTeacherClass(activeSession.teacherClassId)
      const maxIndex = Math.max(0, Math.min(activeSessionCurrentIndex, activeSessionExerciseIds.length - 1))
      const scopeExerciseIds = activeSessionExerciseIds.slice(0, maxIndex + 1)
      const mockRanking = createMockSessionRanking(classStudents, scopeExerciseIds.length, 'provisional')
      const rankingPayload = {
        ...mockRanking,
        scopeExerciseIds,
        sourceSessionUpdatedAtMs: activeSessionUpdatedAtMs
      }
      await saveLiveSessionCurrentRanking(activeSession.id, user.uid, rankingPayload)
      setSessionRanking(rankingPayload)
      setLastRankingRefreshAtMs(Date.now())
      createMockHistoryPreview()
    } catch (err) {
      setError(err.message || 'Impossible de generer des statistiques fictives.')
    } finally {
      setLoadingMockData(false)
    }
  }

  const clearCurrentAggregateStats = async () => {
    if (!activeSession || !user?.uid) return
    setLoadingMockData(true)
    setError('')
    try {
      const [currentRankingDoc, finalRankingDoc] = await Promise.all([
        getLiveSessionCurrentRanking(activeSession.id),
        getLiveSessionFinalRanking(activeSession.id)
      ])
      if (currentRankingDoc?.mock) {
        await clearLiveSessionCurrentRanking(activeSession.id, user.uid)
      }
      if (finalRankingDoc?.mock) {
        await clearLiveSessionFinalRanking(activeSession.id, user.uid)
      }
      setSessionRanking(null)
    } catch (err) {
      setError(err.message || 'Impossible de supprimer les statistiques fictives.')
    } finally {
      setLoadingMockData(false)
    }
  }

  const openSessionRanking = useCallback(async (mode = 'provisional', options = {}) => {
    const { force = false, scope = rankingScope, sourceSessionId = combinedSourceSessionId } = options
    if (!activeSession?.id || !activeSession.teacherClassId || !user?.uid) return
    const isFinal = mode === 'final'
    const now = Date.now()
    if (!isFinal && !force && (now - lastRankingRefreshAtMs) < RANKING_THROTTLE_MS) {
      const remaining = Math.max(1, Math.ceil((RANKING_THROTTLE_MS - (now - lastRankingRefreshAtMs)) / 1000))
      setError(`Classement déjà actualisé récemment. Réessaie dans ${remaining}s.`)
      return
    }
    const maxIndex = Math.max(0, Math.min(activeSessionCurrentIndex, activeSessionExerciseIds.length - 1))
    const rankingExerciseIds = isFinal
      ? activeSessionExerciseIds
      : activeSessionExerciseIds.slice(0, maxIndex + 1)
    if (rankingExerciseIds.length === 0) {
      setSessionRanking({
        ranking: [],
        submissions: 0,
        classSize: 0,
        exerciseCount: 0,
        rankingType: isFinal ? 'final' : 'provisional',
        rankingScope: scope,
        computedAtMs: Date.now(),
        sourceSessionUpdatedAtMs: activeSessionUpdatedAtMs
      })
      return
    }

    setLoadingSessionRanking(true)
    setError('')
    try {
      if (scope === RANKING_SCOPE_COMBINED) {
        const sourceSession = combinedRankingCandidates.find((session) => session.id === sourceSessionId)
        if (!sourceSession) {
          setError('Sélectionnez une session historique avec la même playlist pour activer le classement combiné.')
          return
        }
        const sourceUpdatedAtMs = Number(sourceSession?.summary?.closedAtMs || 0)
          || Number(sourceSession?.closedAtMs || 0)
          || getTimestampMs(sourceSession?.closedAt)
          || getTimestampMs(sourceSession?.updatedAt)
          || 0
        const cached = await getLiveSessionCombinedRanking(activeSession.id, sourceSession.id)
        const cachedFresh = cached
          && Array.isArray(cached?.scopeExerciseIds)
          && cached.scopeExerciseIds.join('|') === rankingExerciseIds.join('|')
          && Number(cached?.sourceSessionUpdatedAtMs?.active || 0) === Number(activeSessionUpdatedAtMs || 0)
          && Number(cached?.sourceSessionUpdatedAtMs?.historical || 0) === Number(sourceUpdatedAtMs || 0)
          && (Date.now() - Number(cached.computedAtMs || 0)) <= RANKING_CACHE_MAX_AGE_MS
        if (cachedFresh && !force) {
          let normalizedRows = applyCompetitionRanking(Array.isArray(cached?.ranking) ? cached.ranking : [])
          normalizedRows = await enrichRankingWithClassPhotos(normalizedRows, activeSession.teacherClassId)
          setSessionRanking({
            ...cached,
            ranking: normalizedRows,
            rankingType: isFinal ? 'final' : 'provisional',
            rankingScope: RANKING_SCOPE_COMBINED,
            combinedWithSessionId: sourceSession.id
          })
          setLastRankingRefreshAtMs(Date.now())
          return
        }

        const [classStudents, attemptsCurrent, attemptsHistorical] = await Promise.all([
          getStudentsByTeacherClass(activeSession.teacherClassId),
          getAttemptsByLiveSession(activeSession.id, {
            exerciseIds: rankingExerciseIds,
            maxCount: 2400
          }),
          getAttemptsByLiveSession(sourceSession.id, {
            exerciseIds: rankingExerciseIds,
            maxCount: 2400
          })
        ])
        const computed = computeSessionRanking(
          [...attemptsCurrent, ...attemptsHistorical],
          classStudents,
          rankingExerciseIds,
          { mergeStrategy: 'best_score' }
        )
        const payload = {
          ...computed,
          ranking: applyCompetitionRanking(computed.ranking),
          scopeExerciseIds: rankingExerciseIds,
          rankingType: isFinal ? 'final' : 'provisional',
          rankingScope: RANKING_SCOPE_COMBINED,
          sourceSessionIds: [activeSession.id, sourceSession.id],
          sourceSessionUpdatedAtMs: {
            active: Number(activeSessionUpdatedAtMs || 0),
            historical: Number(sourceUpdatedAtMs || 0)
          },
          combinedWithSessionId: sourceSession.id,
          computedAtMs: Date.now()
        }
        await saveLiveSessionCombinedRanking(activeSession.id, user.uid, sourceSession.id, payload)
        setSessionRanking(payload)
        setLastRankingRefreshAtMs(Date.now())
        return
      }

      if (!isFinal) {
        const cached = await getLiveSessionCurrentRanking(activeSession.id)
        const cachedFresh = cached
          && Number(cached.sourceSessionUpdatedAtMs || 0) === Number(activeSessionUpdatedAtMs || 0)
          && (Date.now() - Number(cached.computedAtMs || 0)) <= RANKING_CACHE_MAX_AGE_MS
        if (cachedFresh && !force) {
          let normalizedRows = applyCompetitionRanking(Array.isArray(cached?.ranking) ? cached.ranking : [])
          normalizedRows = await enrichRankingWithClassPhotos(normalizedRows, activeSession.teacherClassId)
          setSessionRanking({ ...cached, ranking: normalizedRows, rankingType: 'provisional', rankingScope: RANKING_SCOPE_CURRENT })
          setLastRankingRefreshAtMs(Date.now())
          return
        }
      }

      if (isFinal) {
        const existingFinal = await getLiveSessionFinalRanking(activeSession.id)
        const upToDateFinal = existingFinal
          && Number(existingFinal.sourceSessionUpdatedAtMs || 0) === Number(activeSessionUpdatedAtMs || 0)
        if (upToDateFinal && !force) {
          let normalizedRows = applyCompetitionRanking(Array.isArray(existingFinal?.ranking) ? existingFinal.ranking : [])
          normalizedRows = await enrichRankingWithClassPhotos(normalizedRows, activeSession.teacherClassId)
          setSessionRanking({ ...existingFinal, ranking: normalizedRows, rankingType: 'final', rankingScope: RANKING_SCOPE_CURRENT })
          return
        }
      }

      const [classStudents, attempts] = await Promise.all([
        getStudentsByTeacherClass(activeSession.teacherClassId),
        getAttemptsByLiveSession(activeSession.id, {
          exerciseIds: rankingExerciseIds,
          maxCount: 2400
        })
      ])
      ;(attempts || []).forEach((attempt) => {
        const email = String(attempt?.userEmail || '').toLowerCase()
        const isDavide = email.includes('davide') || email.includes('conte')
        if (!isDavide) return
        const userAnswers = Array.isArray(attempt?.userAnswers) ? attempt.userAnswers : []
        const correctAnswers = Array.isArray(attempt?.correctAnswers) ? attempt.correctAnswers : []
        const maxLen = Math.max(userAnswers.length, correctAnswers.length)
        for (let i = 0; i < maxLen; i += 1) {
          const userLabel = answerLabel(userAnswers[i])
          const correctLabel = answerLabel(correctAnswers[i])
          const normUser = typeof userLabel === 'string' ? userLabel.trim().toLowerCase() : ''
          const normCorrect = typeof correctLabel === 'string' ? correctLabel.trim().toLowerCase() : ''
          const looksTarget = /(^i$|^v7$)/i.test(String(userLabel || '')) || /(^i$|^v7$)/i.test(String(correctLabel || ''))
          const visuallyEqualNormalized = normUser !== '' && normUser === normCorrect
          if (!looksTarget && !visuallyEqualNormalized) continue
          // #region agent log
          fetch('http://127.0.0.1:7245/ingest/35ff180e-d8aa-4991-b134-f4ff94edefe8',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'4cd4b7'},body:JSON.stringify({sessionId:'4cd4b7',runId:'forensic-ranking',hypothesisId:'H1_H2_H5',location:'TeacherLiveSessions.jsx:openSessionRanking:attemptForensics',message:'Forensic compare stored attempt labels',data:{liveSessionId:activeSession.id,exerciseId:String(attempt?.exerciseId||''),attemptId:String(attempt?.id||''),userId:String(attempt?.userId||''),userEmail:String(attempt?.userEmail||''),index:i,storedScore:Number(attempt?.score||0),strictEqual:userLabel===correctLabel,normalizedEqual:visuallyEqualNormalized,userSignature:buildLabelDebugSignature(userLabel),correctSignature:buildLabelDebugSignature(correctLabel)},timestamp:Date.now()})}).catch(()=>{});
          // #endregion
        }
      })
      const computed = computeSessionRanking(attempts, classStudents, rankingExerciseIds)
      const payload = {
        ...computed,
        ranking: applyCompetitionRanking(computed.ranking),
        scopeExerciseIds: rankingExerciseIds,
        rankingType: isFinal ? 'final' : 'provisional',
        rankingScope: RANKING_SCOPE_CURRENT,
        sourceSessionUpdatedAtMs: activeSessionUpdatedAtMs,
        computedAtMs: Date.now()
      }
      if (isFinal) {
        await saveLiveSessionFinalRanking(activeSession.id, user.uid, payload)
      } else {
        await saveLiveSessionCurrentRanking(activeSession.id, user.uid, payload)
      }
      setSessionRanking(payload)
      setLastRankingRefreshAtMs(Date.now())
    } catch (err) {
      setError(err.message || 'Impossible de charger le classement de session.')
    } finally {
      setLoadingSessionRanking(false)
    }
  }, [
    activeSession?.id,
    activeSession?.teacherClassId,
    activeSessionCurrentIndex,
    activeSessionExerciseIds,
    activeSessionUpdatedAtMs,
    combinedRankingCandidates,
    combinedSourceSessionId,
    lastRankingRefreshAtMs,
    rankingScope,
    user?.uid
  ])

  useEffect(() => {
    if (combinedRankingCandidates.length === 0) {
      setCombinedSourceSessionId('')
      if (rankingScope === RANKING_SCOPE_COMBINED) setRankingScope(RANKING_SCOPE_CURRENT)
      return
    }
    setCombinedSourceSessionId((prev) => (
      combinedRankingCandidates.some((session) => session.id === prev)
        ? prev
        : combinedRankingCandidates[0].id
    ))
  }, [combinedRankingCandidates, rankingScope])

  useEffect(() => {
    if (!expandedArchivedSessionId) return
    const el = document.getElementById(`teacher-live-history-ranking-${expandedArchivedSessionId}`)
    if (!el) return
    const t = window.setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }, 100)
    return () => window.clearTimeout(t)
  }, [expandedArchivedSessionId, loadingArchivedRanking, archivedRanking?.sessionId, archivedRankingRows.length, archivedRankingInlineError])

  useEffect(() => {
    setSessionRanking(null)
    setLastRankingRefreshAtMs(0)
    setRankingScope(RANKING_SCOPE_CURRENT)
    setCombinedSourceSessionId('')
  }, [activeSession?.id])

  /** Depuis le lien Player (?view=ranking), faire défiler jusqu’au bloc classement (sous « Session active »). */
  useEffect(() => {
    if (!rankingViewRequested || !activeSession?.id) return
    const scroll = () => {
      liveRankingSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    const raf = window.requestAnimationFrame(scroll)
    const t = window.setTimeout(scroll, 280)
    return () => {
      window.cancelAnimationFrame(raf)
      window.clearTimeout(t)
    }
  }, [
    rankingViewRequested,
    activeSession?.id,
    loadingSessionRanking,
    sessionRanking?.computedAtMs,
    sessionRanking?.ranking?.length
  ])

  useEffect(() => {
    if (!rankingViewRequested || !activeSession?.id || loadingSessionRanking) return
    if (rankingSessionIdRequested && rankingSessionIdRequested !== activeSession.id) return
    const alreadyUpToDate = rankingScope === RANKING_SCOPE_COMBINED
      ? (
        String(sessionRanking?.combinedWithSessionId || '') === String(combinedSourceSessionId || '')
        && Number(sessionRanking?.sourceSessionUpdatedAtMs?.active || 0) === Number(activeSessionUpdatedAtMs || 0)
        && Number(sessionRanking?.sourceSessionUpdatedAtMs?.historical || 0) === Number(combinedSourceSessionUpdatedAtMs || 0)
      )
      : Number(sessionRanking?.sourceSessionUpdatedAtMs || 0) === Number(activeSessionUpdatedAtMs || 0)
    if (alreadyUpToDate) return
    const shouldOpenFinal = activeSession.status === 'correction_released' && isLastExercise
    openSessionRanking(shouldOpenFinal ? 'final' : 'provisional', {
      force: false,
      scope: rankingScope,
      sourceSessionId: combinedSourceSessionId
    })
  }, [
    rankingViewRequested,
    rankingSessionIdRequested,
    activeSession?.id,
    activeSession?.status,
    combinedSourceSessionId,
    combinedSourceSessionUpdatedAtMs,
    isLastExercise,
    loadingSessionRanking,
    rankingScope,
    sessionRanking?.combinedWithSessionId,
    sessionRanking?.sourceSessionUpdatedAtMs,
    activeSessionUpdatedAtMs,
    openSessionRanking
  ])

  const withBusy = async (key, fn) => {
    setBusyAction(key)
    setError('')
    try {
      await fn()
      await loadAll()
    } catch (err) {
      setError(err.message || 'Action impossible.')
    } finally {
      setBusyAction('')
    }
  }

  const switchRankingScope = useCallback((nextScope) => {
    if (nextScope === RANKING_SCOPE_COMBINED && combinedRankingCandidates.length === 0) return
    setRankingScope(nextScope)
    if (!activeSession?.id) return
    const shouldOpenFinal = activeSession.status === 'correction_released' && isLastExercise
    const mode = shouldOpenFinal ? 'final' : 'provisional'
    if (nextScope === RANKING_SCOPE_COMBINED) {
      const fallbackSourceId = combinedSourceSessionId || combinedRankingCandidates[0]?.id || ''
      if (!fallbackSourceId) return
      setCombinedSourceSessionId(fallbackSourceId)
      openSessionRanking(mode, {
        force: false,
        scope: RANKING_SCOPE_COMBINED,
        sourceSessionId: fallbackSourceId
      })
      return
    }
    openSessionRanking(mode, {
      force: false,
      scope: RANKING_SCOPE_CURRENT
    })
  }, [
    activeSession?.id,
    activeSession?.status,
    combinedRankingCandidates,
    combinedSourceSessionId,
    isLastExercise,
    openSessionRanking
  ])

  const duplicateSessionQueue = (session) => {
    const classId = String(session?.teacherClassId || '').trim()
    const rawQueue = Array.isArray(session?.exerciseQueue) ? session.exerciseQueue : []
    const nextQueue = rawQueue
      .map((item, index) => ({
        exerciseId: String(item?.exerciseId || '').trim(),
        exerciseTitle: String(item?.exerciseTitle || '').trim() || 'Exercice',
        order: index
      }))
      .filter((item) => item.exerciseId)
    if (!classId || nextQueue.length === 0) {
      setError('Impossible de dupliquer cette session : playlist ou classe invalide.')
      return
    }
    setSelectedClassId(classId)
    setSelectedInputMode(normalizeLiveInputMode(session?.inputMode))
    setQueueByClass((prev) => ({ ...prev, [classId]: nextQueue }))
  }

  const createMockHistoryPreview = useCallback(() => {
    const classIds = (classes || []).map((item) => item.id).filter(Boolean)
    const defaultClassId = selectedClassId || classIds[0] || ''
    if (!defaultClassId) {
      setError('Aucune classe disponible pour générer un historique fictif.')
      return
    }
    const now = Date.now()
    const baseQueue = (queue.length > 0 ? queue : (activeSessionQueue.length > 0 ? activeSessionQueue : [
      { exerciseId: 'mock-1', exerciseTitle: 'Cadences parfaites', order: 0 },
      { exerciseId: 'mock-2', exerciseTitle: 'Renversements', order: 1 },
      { exerciseId: 'mock-3', exerciseTitle: 'Dominantes secondaires', order: 2 }
    ]))
      .map((item, index) => ({
        exerciseId: String(item?.exerciseId || `mock-${index + 1}`),
        exerciseTitle: String(item?.exerciseTitle || `Exercice ${index + 1}`),
        order: index
      }))
    const fakeRows = Array.from({ length: MOCK_HISTORY_COUNT }).map((_, index) => {
      const classId = classIds[index % Math.max(1, classIds.length)] || defaultClassId
      const exerciseCount = Math.max(1, Math.min(baseQueue.length, 1 + ((index + 2) % Math.max(2, baseQueue.length || 2))))
      const startedAtMs = now - ((index + 1) * 75 * 60000)
      const durationMinutes = 18 + (index * 7)
      const closedAtMs = startedAtMs + (durationMinutes * 60000)
      const classSize = 8 + (index % 5)
      const participants = Math.max(3, classSize - (index % 4))
      const averageScore = Math.max(42, 84 - (index * 6))
      const aboveThresholdPct = Math.max(38, 90 - (index * 11))
      return {
        id: `mock-history-${index + 1}`,
        status: 'closed',
        teacherClassId: classId,
        exerciseQueue: baseQueue.slice(0, exerciseCount),
        currentExerciseIndex: Math.max(0, exerciseCount - 1),
        createdAtMs: startedAtMs,
        closedAtMs,
        summary: {
          participants,
          classSize,
          exerciseCount,
          averageScore,
          aboveThresholdPct,
          topStudentName: `Élève ${index + 1}`,
          topStudentScore: Math.min(99, averageScore + 12),
          durationMinutes,
          closedAtMs
        }
      }
    })
    setMockHistorySessions(fakeRows)
    setHistoryVisibleCount(HISTORY_PAGE_SIZE)
  }, [classes, selectedClassId, queue, activeSessionQueue])

  const closeArchivedRankingPanel = useCallback(() => {
    archivedRankingRequestRef.current += 1
    setExpandedArchivedSessionId(null)
    setArchivedRanking(null)
    setArchivedRankingInlineError(null)
    setLoadingArchivedRanking(false)
  }, [])

  const openArchivedRanking = useCallback(async (session) => {
    if (!session?.id) return
    const reqId = (archivedRankingRequestRef.current += 1)
    setExpandedArchivedSessionId(session.id)
    setArchivedRankingInlineError(null)
    const classLabel = classLabelById[session.teacherClassId] || session.teacherClassId || 'Classe inconnue'
    const fallbackSummary = session?.summary && typeof session.summary === 'object' ? session.summary : {}
    const fallback = {
      sessionId: session.id,
      classLabel,
      rankingType: 'archive',
      computedAtMs: Number(fallbackSummary.closedAtMs || session?.closedAtMs || 0) || getTimestampMs(session?.closedAt) || getTimestampMs(session?.updatedAt),
      rows: [],
      submissions: Number(fallbackSummary.participants || 0),
      classSize: Number(fallbackSummary.classSize || 0),
      exerciseCount: Number(fallbackSummary.exerciseCount || (Array.isArray(session?.exerciseQueue) ? session.exerciseQueue.length : 0)),
      averageScore: Number(fallbackSummary.averageScore || 0),
      aboveThresholdPct: Number(fallbackSummary.aboveThresholdPct || 0),
      topStudentName: String(fallbackSummary.topStudentName || ''),
      topStudentScore: Number(fallbackSummary.topStudentScore || 0)
    }
    setLoadingArchivedRanking(true)
    setArchivedRanking(null)
    setError('')
    try {
      if (String(session.id).startsWith('mock-history-')) {
        if (reqId !== archivedRankingRequestRef.current) return
        setArchivedRanking(fallback)
        return
      }
      const [finalDoc, currentDoc] = await Promise.all([
        getLiveSessionFinalRanking(session.id),
        getLiveSessionCurrentRanking(session.id)
      ])
      if (reqId !== archivedRankingRequestRef.current) return
      const source = (finalDoc && Array.isArray(finalDoc.ranking) && finalDoc.ranking.length > 0)
        ? { doc: finalDoc, type: 'final' }
        : ((currentDoc && Array.isArray(currentDoc.ranking) && currentDoc.ranking.length > 0)
          ? { doc: currentDoc, type: 'provisional' }
          : null)
      if (!source) {
        setArchivedRanking(fallback)
        return
      }
      try {
        const archivedExerciseIds = getSessionExerciseIds(session)
        const archivedAttempts = await getAttemptsByLiveSession(session.id, {
          exerciseIds: archivedExerciseIds,
          maxCount: 2400
        })
        ;(archivedAttempts || []).forEach((attempt) => {
          const email = String(attempt?.userEmail || '').toLowerCase()
          const isDavide = email.includes('davide') || email.includes('conte')
          if (!isDavide) return
          const userAnswers = Array.isArray(attempt?.userAnswers) ? attempt.userAnswers : []
          const correctAnswers = Array.isArray(attempt?.correctAnswers) ? attempt.correctAnswers : []
          const maxLen = Math.max(userAnswers.length, correctAnswers.length)
          for (let i = 0; i < maxLen; i += 1) {
            const userLabel = answerLabel(userAnswers[i])
            const correctLabel = answerLabel(correctAnswers[i])
            const normUser = typeof userLabel === 'string' ? userLabel.trim().toLowerCase() : ''
            const normCorrect = typeof correctLabel === 'string' ? correctLabel.trim().toLowerCase() : ''
            const looksTarget = /(^i$|^v7$)/i.test(String(userLabel || '')) || /(^i$|^v7$)/i.test(String(correctLabel || ''))
            const visuallyEqualNormalized = normUser !== '' && normUser === normCorrect
            if (!looksTarget && !visuallyEqualNormalized) continue
            // #region agent log
            fetch('http://127.0.0.1:7245/ingest/35ff180e-d8aa-4991-b134-f4ff94edefe8',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'4cd4b7'},body:JSON.stringify({sessionId:'4cd4b7',runId:'forensic-archived',hypothesisId:'H1_H2_H3_H5',location:'TeacherLiveSessions.jsx:openArchivedRanking:attemptForensics',message:'Archived ranking forensic compare stored attempt labels',data:{liveSessionId:session.id,exerciseId:String(attempt?.exerciseId||''),attemptId:String(attempt?.id||''),userId:String(attempt?.userId||''),userEmail:String(attempt?.userEmail||''),index:i,storedScore:Number(attempt?.score||0),strictEqual:userLabel===correctLabel,normalizedEqual:visuallyEqualNormalized,userSignature:buildLabelDebugSignature(userLabel),correctSignature:buildLabelDebugSignature(correctLabel)},timestamp:Date.now()})}).catch(()=>{});
            // #endregion
          }
        })
      } catch {}
      let rows = applyCompetitionRanking(Array.isArray(source.doc.ranking) ? source.doc.ranking : [])
      if (session.teacherClassId) {
        rows = await enrichRankingWithClassPhotos(rows, session.teacherClassId)
      }
      if (reqId !== archivedRankingRequestRef.current) return
      const averageScore = rows.length > 0
        ? Math.round(rows.reduce((sum, row) => sum + Number(row?.score || 0), 0) / rows.length)
        : fallback.averageScore
      const aboveThresholdPct = rows.length > 0
        ? Math.round((rows.filter((row) => Number(row?.score || 0) >= SCORE_VISIBLE_THRESHOLD).length / rows.length) * 100)
        : fallback.aboveThresholdPct
      setArchivedRanking({
        sessionId: session.id,
        classLabel,
        rankingType: source.type,
        computedAtMs: Number(source.doc?.computedAtMs || 0) || fallback.computedAtMs,
        rows,
        submissions: Number(source.doc?.submissions || rows.length || fallback.submissions),
        classSize: Number(source.doc?.classSize || fallback.classSize),
        exerciseCount: Number(source.doc?.exerciseCount || fallback.exerciseCount),
        averageScore,
        aboveThresholdPct,
        topStudentName: rows[0]?.studentName || fallback.topStudentName,
        topStudentScore: Number(rows[0]?.score || fallback.topStudentScore)
      })
    } catch (err) {
      if (reqId === archivedRankingRequestRef.current) {
        setArchivedRankingInlineError(err.message || 'Impossible de charger le classement archivé.')
      }
    } finally {
      if (reqId === archivedRankingRequestRef.current) {
        setLoadingArchivedRanking(false)
      }
    }
  }, [classLabelById])

  const closeSessionWithSummary = async () => {
    if (!activeSession?.id || !activeSession?.teacherClassId || !user?.uid) return
    const queueIds = (Array.isArray(activeSession.exerciseQueue) ? activeSession.exerciseQueue : [])
      .map((item) => String(item?.exerciseId || ''))
      .filter(Boolean)
    let summary = null
    try {
      const [classStudents, attempts] = await Promise.all([
        getStudentsByTeacherClass(activeSession.teacherClassId),
        getAttemptsByLiveSession(activeSession.id, { exerciseIds: queueIds, maxCount: 2400 })
      ])
      const computed = computeSessionRanking(attempts, classStudents, queueIds)
      const rows = Array.isArray(computed?.ranking) ? computed.ranking : []
      const avgScore = rows.length > 0
        ? Math.round(rows.reduce((sum, row) => sum + Number(row?.score || 0), 0) / rows.length)
        : 0
      const aboveThresholdPct = rows.length > 0
        ? Math.round((rows.filter((row) => Number(row?.score || 0) >= SCORE_VISIBLE_THRESHOLD).length / rows.length) * 100)
        : 0
      const topStudent = rows[0] || null
      const createdAtMs = getTimestampMs(activeSession?.createdAt)
      const nowMs = Date.now()
      summary = {
        participants: Number(computed?.submissions || 0),
        classSize: Number(computed?.classSize || 0),
        exerciseCount: Number(computed?.exerciseCount || 0),
        averageScore: avgScore,
        aboveThresholdPct,
        topStudentName: topStudent?.studentName || '',
        topStudentScore: Number(topStudent?.score || 0),
        durationMinutes: createdAtMs > 0 ? Math.max(1, Math.round((nowMs - createdAtMs) / 60000)) : 0,
        closedAtMs: nowMs
      }
    } catch (err) {
      summary = null
    }
    await setLiveSessionStatus(activeSession.id, user.uid, 'closed', { summary })
  }

  const removeQueueItem = (exerciseId) => {
    if (!selectedClassId) return
    setQueueByClass((prev) => {
      const current = prev[selectedClassId] || []
      const nextForClass = current
        .filter((item) => item.exerciseId !== exerciseId)
        .map((item, idx) => ({ ...item, order: idx }))
      return { ...prev, [selectedClassId]: nextForClass }
    })
  }

  if (!user || userData?.role !== 'teacher') return null

  return (
    <div className={`teacher-live-page ${rankingViewRequested ? 'teacher-live-page--ranking-focus' : ''}`}>
      <header className="teacher-live-header">
        <button type="button" className="teacher-live-back teacher-live-btn teacher-live-btn--secondary" onClick={() => navigate('/dashboard')}>← Retour</button>
        <h1>Sessions live</h1>
      </header>

      {error && <p className="teacher-live-error">{error}</p>}

      {loading ? (
        <div className="teacher-live-loading"><div className="spinner" /><p>Chargement…</p></div>
      ) : (
        <>
          <section className="teacher-live-panel">
            <h2 className="teacher-live-panel-title">Créer une session live</h2>
            <div className="teacher-live-context">
              <span>Contexte actif</span>
              <strong>{selectedClassLabel ? `Classe ${selectedClassLabel}` : 'Aucune classe sélectionnée'}</strong>
              <button type="button" className="teacher-live-btn teacher-live-btn--secondary" onClick={() => navigate('/dashboard')}>Ajouter des exercices</button>
            </div>
            <div className="teacher-live-row">
              <label>
                Classe
                <select value={selectedClassId} onChange={(e) => setSelectedClassId(e.target.value)}>
                  <option value="">Sélectionner…</option>
                  {classes.map((tc) => <option key={tc.id} value={tc.id}>{tc.name || tc.code || tc.id}</option>)}
                </select>
              </label>
              <label>
                Playlist
                <div className="teacher-live-note">
                  Playlist dédiée a la classe sélectionnée. Tu peux ajouter plusieurs exercices depuis « Mes exercices »
                  sans quitter cette préparation.
                </div>
              </label>
            </div>
            <div className="teacher-live-input-mode">
              <span className="teacher-live-input-mode-label">Mode d'entrée live</span>
              <div className="teacher-live-input-mode-buttons" role="radiogroup" aria-label="Mode d'entrée live">
                {LIVE_INPUT_MODES.map((modeOption) => (
                  <button
                    key={modeOption.value}
                    type="button"
                    role="radio"
                    aria-checked={selectedInputMode === modeOption.value}
                    className={`teacher-live-btn teacher-live-btn--secondary ${selectedInputMode === modeOption.value ? 'is-active' : ''}`}
                    onClick={() => setSelectedInputMode(modeOption.value)}
                    disabled={!!activeSession}
                    title={activeSession ? 'Mode verrouillé tant qu’une session est active.' : ''}
                  >
                    {modeOption.label}
                  </button>
                ))}
              </div>
              <p className="teacher-live-muted">
                Ce mode s’applique à toute la session et reste verrouillé une fois la session ouverte.
              </p>
            </div>

            <div className="teacher-live-queue">
              <h3 className="teacher-live-panel-subtitle">Playlist ({queue.length})</h3>
              {queue.length === 0 ? (
                <p>Aucun exercice sélectionné.</p>
              ) : (
                <ul>
                  {queue.map((item) => (
                    <li key={item.exerciseId}>
                      <span>{item.exerciseTitle}</span>
                      <button type="button" className="teacher-live-btn teacher-live-btn--ghost" onClick={() => removeQueueItem(item.exerciseId)}>Retirer</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <button
              type="button"
              className="teacher-live-primary"
              onClick={createSessionNow}
              disabled={creating || !selectedClassId || queue.length === 0 || !!activeSession}
            >
              {creating ? 'Création…' : (activeSession ? 'Une session est déjà active' : 'Ouvrir la session')}
            </button>
          </section>

          <section className="teacher-live-panel">
            <h2 className="teacher-live-panel-title">Session active</h2>
            {!activeSession ? (
              <p>Aucune session live active.</p>
            ) : (
              <>
                <p>Statut : <strong>{activeSession.status === 'collecting' ? 'Session en cours' : 'Correction publiée'}</strong></p>
                <p>Mode d'entrée : <strong>{getLiveInputModeLabel(activeSession?.inputMode)}</strong></p>
                <p>Exercice courant : <strong>{currentExercise?.exerciseTitle || '—'}</strong></p>
                <div className="teacher-live-actions">
                  {LIVE_REMOTE_ENABLED && (
                    <button
                      type="button"
                      className="teacher-live-btn teacher-live-btn--primary"
                      onClick={() => window.open(
                        `#/dashboard/live-sessions/${encodeURIComponent(String(activeSession.id || ''))}/remote`,
                        '_blank',
                        'noopener,noreferrer'
                      )}
                      disabled={!activeSession?.id}
                    >
                      Ouvrir télécommande mobile
                    </button>
                  )}
                  <button
                    type="button"
                    className="teacher-live-projection-btn"
                    onClick={() => navigate(`/play/${currentExercise?.exerciseId}`, {
                      state: {
                        teacherPreview: true,
                        liveSessionId: activeSession.id,
                        liveCorrectionReleased: activeSession.status === 'correction_released',
                        returnTo: `${location.pathname}${location.search || ''}`
                      }
                    })}
                    disabled={!currentExercise?.exerciseId}
                  >
                    <span aria-hidden>📽️</span>
                    Ouvrir projection
                  </button>
                  <button
                    type="button"
                    className="teacher-live-btn teacher-live-btn--secondary"
                    onClick={() => withBusy('prev', () => rewindLiveSessionExercise(activeSession.id, user.uid))}
                    disabled={busyAction !== '' || Number(activeSession?.currentExerciseIndex || 0) <= 0}
                  >
                    Exercice précédent
                  </button>
                  <button
                    type="button"
                    className="teacher-live-btn teacher-live-btn--secondary"
                    onClick={() => withBusy('next', () => advanceLiveSessionExercise(activeSession.id, user.uid))}
                    disabled={busyAction !== '' || !currentExercise}
                  >
                    Exercice suivant
                  </button>
                  <button
                    type="button"
                    className="teacher-live-btn teacher-live-btn--danger"
                    onClick={() => withBusy('close', closeSessionWithSummary)}
                    disabled={busyAction !== ''}
                  >
                    Fermer la session
                  </button>
                  <button
                    type="button"
                    className="teacher-live-btn teacher-live-btn--primary"
                    onClick={() => {
                      setRankingScope(RANKING_SCOPE_CURRENT)
                      openSessionRanking(canPublishFinalRanking ? 'final' : 'provisional', {
                        scope: RANKING_SCOPE_CURRENT
                      })
                      window.requestAnimationFrame(() => {
                        liveRankingSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
                      })
                    }}
                    disabled={loadingSessionRanking}
                  >
                    {loadingSessionRanking
                      ? (canPublishFinalRanking ? 'Publication…' : 'Chargement…')
                      : 'Voir classement'}
                  </button>
                  <button type="button" className="teacher-live-btn teacher-live-btn--secondary" onClick={injectMockCurrentStats} disabled={loadingMockData || !currentExercise?.exerciseId}>
                    {loadingMockData ? 'Chargement…' : 'Injecter classement fictif'}
                  </button>
                  <button type="button" className="teacher-live-btn teacher-live-btn--ghost" onClick={clearCurrentAggregateStats} disabled={loadingMockData || !activeSession?.id}>
                    Supprimer données fictives
                  </button>
                </div>

                {(sessionRanking || loadingSessionRanking || rankingViewRequested) && (
                  <div
                    ref={liveRankingSectionRef}
                    id="teacher-live-ranking-section"
                    className="teacher-live-stats-wrap teacher-live-stats-wrap--ranking-anchor"
                  >
                    <div className="teacher-live-ranking-head">
                      <h3 className="teacher-live-ranking-title">
                        Classement général
                        {sessionRanking?.rankingScope === RANKING_SCOPE_COMBINED && (
                          <span className="teacher-live-ranking-badge is-provisional">Combiné</span>
                        )}
                        {sessionRanking?.rankingType === 'final' && (
                          <span className="teacher-live-ranking-badge is-final">Final</span>
                        )}
                        {sessionRanking?.mock && (
                          <span className="teacher-live-ranking-badge is-mock">Données fictives</span>
                        )}
                      </h3>
                      <div className="teacher-live-ranking-head-actions">
                        <div className="teacher-live-ranking-scope">
                          <button
                            type="button"
                            className={`teacher-live-btn teacher-live-btn--secondary ${rankingScope === RANKING_SCOPE_CURRENT ? 'is-active' : ''}`}
                            onClick={() => switchRankingScope(RANKING_SCOPE_CURRENT)}
                            disabled={loadingSessionRanking}
                          >
                            Session en cours
                          </button>
                          <button
                            type="button"
                            className={`teacher-live-btn teacher-live-btn--secondary ${rankingScope === RANKING_SCOPE_COMBINED ? 'is-active' : ''}`}
                            onClick={() => switchRankingScope(RANKING_SCOPE_COMBINED)}
                            disabled={loadingSessionRanking || combinedRankingCandidates.length === 0}
                            title={combinedRankingCandidates.length === 0 ? 'Aucune session historique compatible (playlist strictement identique).' : ''}
                          >
                            Combiné
                          </button>
                          {rankingScope === RANKING_SCOPE_COMBINED && combinedRankingCandidates.length > 0 && (
                            <select
                              value={combinedSourceSessionId}
                              onChange={(event) => {
                                const nextSourceId = String(event.target.value || '')
                                setCombinedSourceSessionId(nextSourceId)
                                const shouldOpenFinal = activeSession?.status === 'correction_released' && isLastExercise
                                openSessionRanking(shouldOpenFinal ? 'final' : 'provisional', {
                                  force: false,
                                  scope: RANKING_SCOPE_COMBINED,
                                  sourceSessionId: nextSourceId
                                })
                              }}
                              disabled={loadingSessionRanking}
                            >
                              {combinedRankingCandidates.map((session) => (
                                <option key={`combined-source-${session.id}`} value={session.id}>
                                  {formatSessionDateTime(Number(session?.summary?.closedAtMs || 0) || Number(session?.closedAtMs || 0) || getTimestampMs(session?.closedAt) || getTimestampMs(session?.updatedAt))}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                        <span className="teacher-live-muted">Maj: {formatRankingUpdateTime(sessionRanking?.computedAtMs)}</span>
                        <button
                          type="button"
                          className="teacher-live-btn teacher-live-btn--secondary"
                          onClick={() => openSessionRanking(sessionRanking?.rankingType === 'final' ? 'final' : 'provisional', {
                            force: true,
                            scope: rankingScope,
                            sourceSessionId: combinedSourceSessionId
                          })}
                          disabled={loadingSessionRanking}
                        >
                          {loadingSessionRanking ? 'Chargement…' : 'Rafraîchir'}
                        </button>
                      </div>
                    </div>

                    <div className="teacher-live-ranking-kpis">
                      <article><span>Participants</span><strong>{sessionRanking?.submissions || 0}/{sessionRanking?.classSize || 0}</strong></article>
                      <article><span>Exercices</span><strong>{sessionRanking?.exerciseCount || 0}</strong></article>
                      <article><span>Moyenne classe</span><strong>{rankingAverage}%</strong></article>
                      <article><span>Au-dessus 40%</span><strong>{rankingAboveThresholdPct}%</strong></article>
                    </div>
                    {combinedRankingCandidates.length === 0 && (
                      <p className="teacher-live-muted">
                        Mode combiné indisponible: aucune session historique fermée avec une playlist strictement identique.
                      </p>
                    )}

                    {rankingRows.length === 0 ? (
                      loadingSessionRanking ? (
                        <SessionRankingLeaderboardBlock rows={[]} exerciseCount={0} loading />
                      ) : (
                        <div className="teacher-live-ranking-empty">
                          <p className="teacher-live-muted">Aucune réponse pour le périmètre actuel.</p>
                          <button type="button" onClick={injectMockCurrentStats} disabled={loadingMockData || !currentExercise?.exerciseId}>
                            Injecter données fictives
                          </button>
                        </div>
                      )
                    ) : (
                      <SessionRankingLeaderboardBlock
                        key={`live-rank-${activeSession?.id || 'none'}`}
                        rows={rankingRows}
                        exerciseCount={sessionRanking?.exerciseCount || 0}
                        loading={false}
                      />
                    )}
                    <p className="teacher-live-muted">
                      Basé sur {sessionRanking?.exerciseCount || 0} exercice(s) et {sessionRanking?.submissions || 0} participation(s).
                      {sessionRanking?.rankingScope === RANKING_SCOPE_COMBINED && sessionRanking?.combinedWithSessionId && (
                        <> Périmètre: session active + session historique sélectionnée.</>
                      )}
                    </p>
                  </div>
                )}
              </>
            )}
          </section>

          <section className="teacher-live-panel">
            <h2 className="teacher-live-panel-title">Historique récent</h2>
            <div className="teacher-live-history-toolbar">
              <button type="button" className="teacher-live-btn teacher-live-btn--secondary" onClick={createMockHistoryPreview}>
                {mockHistorySessions.length > 0 ? 'Régénérer aperçu fictif' : 'Aperçu historique fictif'}
              </button>
              {mockHistorySessions.length > 0 && (
                <button type="button" className="teacher-live-btn teacher-live-btn--ghost" onClick={() => setMockHistorySessions([])}>
                  Retirer aperçu fictif
                </button>
              )}
            </div>
            {historySessions.length === 0 ? (
              <p className="teacher-live-muted">Aucune session clôturée pour le moment.</p>
            ) : (
              <>
                <ul className="teacher-live-history-cards">
                  {visibleHistorySessions.map((s) => {
                    const queueSize = Array.isArray(s.exerciseQueue) ? s.exerciseQueue.length : 0
                    const completedCount = Math.min(queueSize, Math.max(0, Number(s.currentExerciseIndex || 0) + 1))
                    const meta = statusMeta(s.status)
                    const createdAtMs = Number(s?.createdAtMs || 0) || getTimestampMs(s.createdAt)
                    const closedAtMs = Number(s?.summary?.closedAtMs || 0) || Number(s?.closedAtMs || 0) || getTimestampMs(s.closedAt) || getTimestampMs(s.updatedAt)
                    const classLabel = classLabelById[s.teacherClassId] || s.teacherClassId || 'Classe inconnue'
                    const participants = Number(s?.summary?.participants || 0)
                    const classSize = Number(s?.summary?.classSize || 0)
                    const avgScore = Number(s?.summary?.averageScore || 0)
                    const aboveThreshold = Number(s?.summary?.aboveThresholdPct || 0)
                    const hasSummary = !!(s && s.summary && typeof s.summary === 'object')
                    const isArchiveOpenHere = expandedArchivedSessionId === s.id && (
                      loadingArchivedRanking
                      || String(archivedRanking?.sessionId || '') === String(s.id)
                      || (!!archivedRankingInlineError && !loadingArchivedRanking)
                    )
                    return (
                      <li key={s.id} className="teacher-live-history-card-item">
                        <div className="teacher-live-history-card">
                          <div className="teacher-live-history-head">
                            <strong>{classLabel}</strong>
                            <span className={`teacher-live-status-pill tone-${meta.tone}`}>{meta.label}</span>
                          </div>
                          <div className="teacher-live-history-meta">
                            <span>Début: {formatSessionDateTime(createdAtMs)}</span>
                            <span>Fin: {formatSessionDateTime(closedAtMs)}</span>
                            <span>Durée: {formatSessionDuration(Number(s?.summary?.durationMinutes || 0))}</span>
                          </div>
                          <div className="teacher-live-history-kpis">
                            <span>Progression: <strong>{completedCount}/{queueSize}</strong></span>
                            <span>Participants: <strong>{hasSummary ? `${participants}/${classSize}` : '—'}</strong></span>
                            <span>Moyenne: <strong>{hasSummary ? `${avgScore}%` : '—'}</strong></span>
                            <span>Au-dessus 40%: <strong>{hasSummary ? `${aboveThreshold}%` : '—'}</strong></span>
                          </div>
                          <div className="teacher-live-history-actions">
                            <button type="button" className="teacher-live-btn teacher-live-btn--secondary" onClick={() => duplicateSessionQueue(s)}>
                              Dupliquer playlist
                            </button>
                            <button
                              type="button"
                              className={`teacher-live-btn ${isArchiveOpenHere ? 'teacher-live-btn--secondary' : 'teacher-live-btn--primary'}`}
                              aria-expanded={isArchiveOpenHere}
                              onClick={() => {
                                if (isArchiveOpenHere) {
                                  closeArchivedRankingPanel()
                                  return
                                }
                                openArchivedRanking(s)
                              }}
                            >
                              {loadingArchivedRanking && expandedArchivedSessionId === s.id
                                ? 'Chargement…'
                                : isArchiveOpenHere
                                  ? 'Masquer le classement'
                                  : 'Voir classement'}
                            </button>
                          </div>
                          {s?.summary?.topStudentName && (
                            <p className="teacher-live-muted">
                              Top session: {s.summary.topStudentName} ({Number(s.summary.topStudentScore || 0)}%).
                            </p>
                          )}
                        </div>
                        {isArchiveOpenHere && (
                          <div
                            id={`teacher-live-history-ranking-${s.id}`}
                            className="teacher-live-history-ranking teacher-live-history-ranking--below-card"
                          >
                            <div className="teacher-live-ranking-head">
                              <h3 className="teacher-live-ranking-title teacher-live-ranking-title--archived">
                                Classement archivé — {classLabel}
                                {loadingArchivedRanking ? (
                                  <span className="teacher-live-ranking-badge is-provisional">Chargement…</span>
                                ) : archivedRankingInlineError ? (
                                  <span className="teacher-live-ranking-badge is-mock">Erreur</span>
                                ) : (
                                  <span className={`teacher-live-ranking-badge ${archivedRanking?.rankingType === 'final' ? 'is-final' : 'is-provisional'}`}>
                                    {archivedRanking?.rankingType === 'final' ? 'Final' : (archivedRanking?.rankingType === 'provisional' ? 'Enregistré' : 'Synthèse')}
                                  </span>
                                )}
                              </h3>
                              <div className="teacher-live-ranking-head-actions teacher-live-ranking-head-actions--archived-inline">
                                <span className="teacher-live-muted">
                                  Maj: {archivedRanking?.computedAtMs ? formatRankingUpdateTime(archivedRanking.computedAtMs) : '—'}
                                </span>
                                <button
                                  type="button"
                                  className="teacher-live-btn teacher-live-btn--ghost"
                                  onClick={closeArchivedRankingPanel}
                                >
                                  Fermer
                                </button>
                              </div>
                            </div>
                            {archivedRankingInlineError && !loadingArchivedRanking ? (
                              <div className="teacher-live-history-ranking-error">
                                <p className="teacher-live-error" style={{ margin: '0 0 10px' }}>{archivedRankingInlineError}</p>
                                <button
                                  type="button"
                                  className="teacher-live-btn teacher-live-btn--secondary"
                                  onClick={() => openArchivedRanking(s)}
                                >
                                  Réessayer
                                </button>
                              </div>
                            ) : (
                              <>
                                {!loadingArchivedRanking && (
                                  <div className="teacher-live-ranking-kpis">
                                    <article><span>Participants</span><strong>{archivedRanking?.submissions || 0}/{archivedRanking?.classSize || 0}</strong></article>
                                    <article><span>Exercices</span><strong>{archivedRanking?.exerciseCount || 0}</strong></article>
                                    <article><span>Moyenne classe</span><strong>{archivedRankingAverage}%</strong></article>
                                    <article><span>Au-dessus 40%</span><strong>{archivedRankingAboveThresholdPct}%</strong></article>
                                  </div>
                                )}
                                {archivedRankingRows.length === 0 ? (
                                  loadingArchivedRanking ? (
                                    <SessionRankingLeaderboardBlock
                                      rows={[]}
                                      exerciseCount={Number(archivedRanking?.exerciseCount) || Number(s?.summary?.exerciseCount) || queueSize || 0}
                                      loading
                                    />
                                  ) : (
                                    <p className="teacher-live-muted">
                                      Aucun classement détaillé sauvegardé pour cette session. Synthèse: {archivedRanking?.topStudentName
                                        ? `${archivedRanking.topStudentName} (${archivedRanking.topStudentScore}%)`
                                        : 'aucun top élève disponible'}.
                                    </p>
                                  )
                                ) : (
                                  <SessionRankingLeaderboardBlock
                                    key={`arch-rank-${archivedRanking?.sessionId || 'none'}`}
                                    rows={archivedRankingRows}
                                    exerciseCount={archivedRanking?.exerciseCount || 0}
                                    loading={false}
                                  />
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </li>
                    )
                  })}
                </ul>
                {historyHasMore && (
                  <button
                    type="button"
                    className="teacher-live-btn teacher-live-btn--secondary teacher-live-history-more"
                    onClick={() => setHistoryVisibleCount((prev) => prev + HISTORY_PAGE_SIZE)}
                  >
                    Afficher plus
                  </button>
                )}
              </>
            )}
          </section>
        </>
      )}
    </div>
  )
}

export default TeacherLiveSessions

